import type { ComputedRef } from 'vue';
import { useRoute } from 'vue-router';
import type { DesignFeature } from '~/composables/ds/useFeatureVersion';

// Version DS de l'ÉCRAN routé, à ne pas confondre avec celle du chrome (useChromeVersion) : le
// chrome v2 habille aussi des écrans encore legacy - c'est le sens de la migration feature par
// feature (cf. FEATURES_REQUIRING_CHROME_V2). Ces écrans-là gardent leurs contraintes de layout
// legacy, notamment leur propre conteneur de scroll : Documents et Analyse enferment leurs
// panneaux dans une hauteur fixe et scrollent à l'intérieur. Leur retirer ce cadre parce que le
// CHROME est en v2 fait déborder la liste et le viewer PDF sur toute la hauteur du document.
//
// Le nom de route porte le segment de l'onglet dossier (`opportunities-opportunityHashId-<seg>-…`).
// Le préfixe est ancré : `opportunity-simulation` (page hors dossier) contient `-simulation` sans
// être l'écran Simulations du dossier.
const DOSSIER_ROUTE_PREFIX = 'opportunities-opportunityHashId-';

const ROUTE_SEGMENT_FEATURES: ReadonlyArray<readonly [string, DesignFeature]> = [
  ['simulation', 'simulation'],
  ['analysis', 'analyse'],
  ['documents', 'documents'],
  ['messages', 'messages'],
  ['edit', 'edition'],
];

// Une feature dont seuls certains écrans sont portés ne déclare v2 que ceux-là : les autres gardent le
// cadre et le scroll legacy sous le flag ouvert. Absente d'ici, toute la feature est portée.
const PORTED_ROUTE_SEGMENTS: Partial<Record<DesignFeature, readonly string[]>> = {
  analyse: ['analysis-synthesis'],
};

export function useScreenVersion (): {
  screenFeature: ComputedRef<DesignFeature | null>;
  screenV2: ComputedRef<boolean>;
} {
  const route = useRoute();
  const { isV2 } = useFeatureVersion();

  const screenFeature = computed<DesignFeature | null>(() => {
    const name = String(route.name ?? '');
    return ROUTE_SEGMENT_FEATURES
      .find(([segment]) => name.startsWith(`${DOSSIER_ROUTE_PREFIX}${segment}`))?.[1] ?? null;
  });

  // Un écran sans feature DS (liste de dossiers, admin, aperçu…) est legacy par définition.
  const screenV2 = computed<boolean>(() => {
    const feature = screenFeature.value;
    if (feature === null || !isV2(feature)) { return false; }
    const ported = PORTED_ROUTE_SEGMENTS[feature];
    const name = String(route.name ?? '');
    return !ported || ported.some(segment => name.startsWith(`${DOSSIER_ROUTE_PREFIX}${segment}`));
  });

  return { screenFeature, screenV2 };
}
